/**
 * Návrh hodnoty podle zvolené metody.
 *
 * Vykreslí můstek z /api/navrh: kroky, u každého řádku částku, důvod
 * a zdroj (závěrka, odpověď klienta, valuační jádro, výpočet). Hodnoty
 * jsou v tisících Kč. Co chybí, se vypíše, nic se nedopočítává.
 */
import React, { useEffect, useState } from 'react';
import { navrhHodnoty, upravyZOcisteni } from '../api/rozbor';
import { buildValuationRequest } from '../api/valuationCalculator';

const tis = v => (v === null || v === undefined ? '' : `${Math.round(v).toLocaleString('cs-CZ')} tis. Kč`);
const nasobek = v => `× ${Number(v).toFixed(2).replace('.', ',')}`;

function Krok({ krok }) {
  return (
    <div style={{ marginBottom: '1rem' }}>
      <div style={{ fontSize: '0.72rem', letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--text-dim)', fontWeight: 600, marginBottom: '0.4rem' }}>
        {krok.nazev}
      </div>
      {krok.radky.map((r, i) => (
        <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '0.75rem', padding: '0.35rem 0', borderBottom: '1px solid rgba(0,0,0,0.06)', opacity: r.mezisoucet ? 0.7 : 1 }}>
          <div>
            <div style={{ fontSize: '0.88rem', color: 'var(--star-white)', fontStyle: r.mezisoucet ? 'italic' : 'normal' }}>{r.popis}</div>
            {r.poznamka && <div style={{ fontSize: '0.76rem', color: 'var(--text-dim)' }}>{r.poznamka}</div>}
            <div style={{ fontSize: '0.7rem', color: 'var(--purple-glow)' }}>Zdroj: {r.zdroj}</div>
          </div>
          <div style={{ fontSize: '0.88rem', fontWeight: 500, color: 'var(--star-white)', textAlign: 'right', whiteSpace: 'nowrap' }}>
            {r.operace === 'x' ? nasobek(r.castka) : tis(r.castka)}
          </div>
        </div>
      ))}
      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.45rem 0', fontWeight: 600, fontSize: '0.92rem', color: 'var(--star-white)' }}>
        <span>{krok.souhrn}</span><span>{tis(krok.vysledek)}</span>
      </div>
    </div>
  );
}

export function NavrhHodnoty({ navrh, nadpis = 'Návrh hodnoty podle zvolené metody', poznamkaJadra = null, bezMetody = false }) {
  if (!navrh || !navrh.metoda) return null;
  const hlavni = navrh.hodnota_pro_majitele ?? navrh.hodnota_provozu;
  return (
    <div style={{ background: 'var(--purple-deep)', borderRadius: 12, padding: '1rem 1.1rem', marginBottom: '1rem' }}>
      <div style={{ fontWeight: 600, color: 'var(--star-white)', marginBottom: '0.3rem' }}>{nadpis}</div>
      {!bezMetody && (
        <div style={{ fontSize: '0.85rem', color: 'var(--text-dim)', marginBottom: '0.8rem' }}>
          Metoda: <strong style={{ color: 'var(--star-white)' }}>{navrh.metoda.nazev}</strong>. {navrh.metoda.duvody.join(' ')}
        </div>
      )}

      {hlavni !== null && hlavni !== undefined && (
        <div style={{ textAlign: 'center', padding: '0.8rem', borderRadius: 10, background: 'var(--card-bg, white)', marginBottom: '1rem' }}>
          <div style={{ fontSize: '0.72rem', letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--text-dim)' }}>
            {navrh.hodnota_pro_majitele != null ? 'Hodnota pro majitele' : 'Hodnota provozu'}
          </div>
          <div style={{ fontFamily: "'Source Serif 4', Georgia, serif", fontSize: '1.9rem', color: 'var(--star-white)' }}>{tis(hlavni)}</div>
          {navrh.rozpeti && (
            <div style={{ fontSize: '0.82rem', color: 'var(--text-dim)' }}>
              Rozpětí {tis(navrh.rozpeti.od)} až {tis(navrh.rozpeti.do)} ({navrh.rozpeti.zdroj})
            </div>
          )}
        </div>
      )}

      {navrh.kroky.map((k, i) => <Krok key={i} krok={k} />)}

      {navrh.kontrola_zdola && (
        <div className="info-message" style={{ marginBottom: '0.75rem' }}>
          {navrh.kontrola_zdola.vynosova != null ? (
            <><strong>Kontrola výnosem:</strong> výnosová hodnota {tis(navrh.kontrola_zdola.vynosova)}.</>
          ) : (
            <><strong>Kontrola majetkovým přístupem:</strong> čistý majetek {tis(navrh.kontrola_zdola.cisty_majetek)} ({navrh.kontrola_zdola.zdroj}).</>
          )}
          {navrh.kontrola_zdola.zaver ? ` ${navrh.kontrola_zdola.zaver}` : ''}
        </div>
      )}

      {navrh.trh && (
        <div className="info-message" style={{ marginBottom: '0.75rem' }}>
          <strong>Kontrola tržním násobkem:</strong>{' '}
          {navrh.trh.nasobek
            ? <>
                průměrný násobek EBITDA pro firmy této velikosti (EBITDA přibližně{' '}
                {Number(navrh.trh.ebitda_eur).toLocaleString('cs-CZ')} EUR) je {nasobek(navrh.trh.nasobek)},
                {' '}při něm by hodnota provozu činila {tis(navrh.trh.hodnota_pri_trznim)}.
                {navrh.trh.nasobek_kalkulacky ? ` Násobek ocenění je ${nasobek(navrh.trh.nasobek_kalkulacky)}.` : ''}
                {navrh.trh.zaver ? ` ${navrh.trh.zaver}` : ''}
                {navrh.trh.poznamka ? ` ${navrh.trh.poznamka}` : ''}
              </>
            : navrh.trh.duvod}
          {navrh.trh.zdroj && (
            <div style={{ fontSize: '0.72rem', color: 'var(--text-dim)', marginTop: '0.3rem' }}>
              Zdroj: {navrh.trh.zdroj}{navrh.trh.kurz ? `, ${navrh.trh.kurz.zdroj}` : ''}. {navrh.trh.omezeni}
            </div>
          )}
          {navrh.trh.upozorneni && <div className="chyba-pole" style={{ marginTop: '0.3rem' }}>{navrh.trh.upozorneni}</div>}
        </div>
      )}

      {navrh.podil && (
        <div className="info-message" style={{ marginBottom: '0.75rem' }}>
          <strong>Podíl {navrh.podil.procent} %:</strong>{' '}
          {navrh.podil.pomerna_cast != null ? `poměrná část hodnoty pro majitele činí ${tis(navrh.podil.pomerna_cast)}. ` : ''}
          {navrh.podil.poznamka || ''}
        </div>
      )}

      {poznamkaJadra && <div className="info-message" style={{ marginBottom: '0.75rem' }}>{poznamkaJadra}</div>}

      {navrh.chybi.length > 0 && (
        <div className="warning-message" style={{ marginBottom: '0.75rem' }}>
          {navrh.chybi.map(c => <div key={c}>{c}</div>)}
        </div>
      )}
      {navrh.poznamky.map(p => <p key={p} style={{ fontSize: '0.8rem', color: 'var(--text-dim)', margin: '0.3rem 0 0' }}>{p}</p>)}
    </div>
  );
}

/** Návrh ve výsledcích: výkaz z rozboru (nebo z financí) a násobek z jádra. */
export function NavrhZVysledku({ wizardState }) {
  // Návrh přichází s výsledkem výpočtu, aby karta a hlavní číslo byly
  // jedno a totéž. Samostatný dotaz jen u starších uložených valuací.
  const zVysledku = wizardState.valuationResult && wizardState.valuationResult.navrh;
  const [navrh, setNavrh] = useState(zVysledku || null);
  const [chyba, setChyba] = useState(null);
  useEffect(() => {
    if (zVysledku) { setNavrh(zVysledku); return undefined; }
    let platne = true;
    const rozbor = wizardState.zadani && wizardState.zadani.rozbor;
    navrhHodnoty({
      vykaz: rozbor ? rozbor.vykaz : null,
      typPodnikani: wizardState.typ_podnikani,
      odpovedi: { ...(rozbor ? rozbor.odpovedi : {}), ...upravyZOcisteni(wizardState.profitCleaning) },
      valuationRequest: buildValuationRequest(wizardState),
    }).then(n => { if (platne) setNavrh(n); }).catch(e => { if (platne) setChyba(e.message); });
    return () => { platne = false; };
  }, [wizardState.valuationResult]); // eslint-disable-line react-hooks/exhaustive-deps

  if (chyba) return null;
  if (!navrh) return null;
  const pozn = navrh.metoda && !['ebitda', 'sde'].includes(navrh.metoda.klic)
    ? 'U této firmy je hlavní metodou ocenění ' + navrh.metoda.nazev.toLowerCase()
      + '. Hodnota níže z modelu kvality podnikání je doplňkový pohled přes zisk.'
    : null;
  return <NavrhHodnoty navrh={navrh} poznamkaJadra={pozn} />;
}
